// Multi-Select Question Component
// Handles user interaction for multi-select trivia questions
// Supports partial credit scoring
// Follows the Section, Component & Manager System architecture

import React, { useState } from 'react';

export function MultiSelectQuestion({
  trivia,
  selectedAnswers,
  showResults,
  onAnswerSelect,
  onSubmitAnswer,
  disabled = false
}) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!selectedAnswers || selectedAnswers.length === 0 || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await onSubmitAnswer(selectedAnswers);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOptionClick = (optionIndex) => {
    if (showResults || disabled) return;
    onAnswerSelect(optionIndex);
  };

  const options = trivia.questionData?.options || [];
  const correctAnswers = trivia.questionData?.correctAnswers || [];
  const partialCredit = trivia.questionData?.partialCredit || false;

  return (
    <div className="learn-trivia-options">
      <div className="multi-select-instructions">
        <small>Select all that apply</small>
        {partialCredit && <small className="partial-credit-note">Partial credit available</small>}
      </div>

      <div className="mc-options-grid">
        {options.map((option, index) => {
          const isSelected = selectedAnswers && selectedAnswers.includes(index);
          const isCorrect = correctAnswers.includes(index);
          const isCorrectSelection = showResults && isSelected && isCorrect;
          const isIncorrectSelection = showResults && isSelected && !isCorrect;
          const isMissedCorrect = showResults && !isSelected && isCorrect;

          return (
            <div
              key={index}
              className={`mc-option-card ${
                isSelected ? 'selected' : ''
              } ${
                showResults ? (
                  isCorrectSelection ? 'correct' :
                  (isIncorrectSelection || isMissedCorrect) ? 'incorrect' : ''
                ) : ''
              }`}
              onClick={() => handleOptionClick(index)}
            >
              <div className="mc-option-header">
                <div className={`mc-checkbox ${isSelected ? 'checked' : ''}`}>
                  <i className="fa-regular fa-square"></i>
                  {isSelected && <i className="fa-solid fa-check mc-check-overlay"></i>}
                </div>
              </div>

              <div className="mc-option-content">
                <p className="mc-option-text">{option.text || option}</p>
              </div>

            </div>
          );
        })}
      </div>

      {!showResults && selectedAnswers && selectedAnswers.length > 0 && (
        <div className="learn-submit-section">
          <button
            className="learn-submit-btn"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <i className="fa-solid fa-spinner fa-spin" style={{ marginRight: '8px' }}></i>
                Submitting...
              </>
            ) : (
              `Submit ${selectedAnswers.length} Selection${selectedAnswers.length !== 1 ? 's' : ''}`
            )}
          </button>
        </div>
      )}

      {!showResults && (!selectedAnswers || selectedAnswers.length === 0) && (
        <div className="multi-select-hint">
          <small>Please select at least one option</small>
        </div>
      )}

      {showResults && (
        <div className="learn-trivia-result">
          <div className="multi-select-results">
            <div className="result-summary">
              <strong>You selected {selectedAnswers ? selectedAnswers.length : 0} option{selectedAnswers && selectedAnswers.length !== 1 ? 's' : ''}</strong>
              {correctAnswers.length > 1 && (
                <span> • {correctAnswers.length} correct answers total</span>
              )}
            </div>
            {partialCredit && (
              <div className="partial-credit-info">
                <small>Partial credit available for correct selections</small>
              </div>
            )}
          </div>
          <div className="learn-explanation">
            <strong>Explanation:</strong> {trivia.explanation}
          </div>
        </div>
      )}
    </div>
  );
}